import React, {useState, useEffect, useMemo} from 'react';
import s from '../../s';
import {observer} from 'mobx-react';
import LevelLockModal from '../modals/level-lock.modal';
import SmartBoxModal from '../modals/smart-box.modal';
import {isNullOrWhiteSpace, Portal, roundF} from '../../../util';

declare const collapse: {
    init: () => void;
};

const DescMainContent: React.FC = observer(() => {
    const [description] = useState<string>('');
    const [init, setInit] = useState<boolean>(false);

    useEffect(() => {
        if (!init) {
            setInit(true);
            collapse.init();
        }
    });

    const hide_dynamic = useMemo((): boolean => {
        if (!s.ready) {
            return false;
        }

        return [70214, 70215, 70244, 84054].includes(s.prod.ProductID) || ['391332'].includes(s.prod.ItemID);
    }, []);

    const show_doc_size = useMemo((): boolean => {
        if (!s.ready) {
            return false;
        }

        return s.ps.docWidth >= 0 && s.ps.docHeight >= 0 && !s.prod.Name.includes('collage') && s.prod.ProductTypeID != 25 && s.prod.IsFrame;
    }, []);

    const show_frame_size = useMemo((): boolean => {
        if (!s.ready) {
            return false;
        }

        return s.prod.JF_Width > 0;
    }, []);

    const show_custom_frame = useMemo((): boolean => {
        if (!s.ready) {
            return false;
        }

        return s.prod.IsFrame && s.prod.ProductTypeID !== 67;
    }, []);

    const show_smart_box = useMemo((): boolean => {
        if (!s.ready) {
            return false;
        }

        if (!s.ui.allow_smartbox) {
            return false;
        }

        return s.prod.Shipping_BoxID <= 3 && s.prod.IsFrame && !s.prod.ProductTypeDisplayName.toLowerCase().includes('Photo') && !hide_dynamic;
    }, [hide_dynamic]);

    const jf_width = useMemo((): number | false => {
        if (!s.ready || !show_frame_size) {
            return false;
        }

        return roundF(s.prod.JF_Width - s.prod.docWidth + s.ps.docWidth + s.ps.moulding_extWidth * 2, 1);
    }, [show_frame_size]);

    const jf_height = useMemo((): number | false => {
        if (!s.ready || !show_frame_size) {
            return false;
        }

        return roundF(s.prod.JF_Height - s.prod.docHeight + s.ps.docHeight + s.ps.moulding_extWidth * 2, 1);
    }, [show_frame_size]);

    return (
        <>
            {s.si && s.si.StoreID !== 205255 && !s.si.has_bullet_override && (
                <div
                    dangerouslySetInnerHTML={{
                        __html: description || s.si.default_prod_info_static,
                    }}
                />
            )}
            {!hide_dynamic && s.si.StoreID !== 205255 && !s.si.has_bullet_override && (
                <ul>
                    {!s.prod.IsCAF && show_doc_size && <li>{`${s.si.ph_desc_size} ${s.ps.docWidth}"w x ${s.ps.docHeight}"h`}</li>}
                    {!s.prod.IsCAF && show_frame_size && <li>{`${s.si.ph_desc_jf_size} ${jf_width}"w x ${jf_height}"h`}</li>}

                    {!s.prod.IsCAF && s.prod.LevelLock && (Portal?.PortalID === 0 || !Portal?.DisableProdDescLinks) && !s.prod.HideLevelLock && (
                        <li>
                            {s.si.ph_desc_ll} <LevelLockModal />
                        </li>
                    )}

                    {!s.prod.IsCAF && s.prod.LevelLock && (Portal?.PortalID !== 0 || Portal?.DisableProdDescLinks) && !s.prod.HideLevelLock && <li>{s.si.ph_desc_ll} Level-Lock Frame Hanging System!</li>}

                    {!s.prod.IsCAF && show_smart_box && (Portal?.PortalID === 0 || !Portal?.DisableProdDescLinks) && (
                        <li>
                            {s.si.ph_desc_smart} <SmartBoxModal />
                        </li>
                    )}

                    {!s.prod.IsCAF && show_smart_box && (Portal?.PortalID !== 0 || Portal?.DisableProdDescLinks) && <li>{s.si.ph_desc_smart} SMARTbox package</li>}

                    {!s.prod.IsCAF && show_custom_frame && s.si.StoreTypeID === 5 && <li>This custom frame is handcrafted in the USA.</li>}
                    {!s.prod.IsCAF && show_custom_frame && s.si.StoreTypeID !== 5 && <li>{s.si.ph_desc_custom}</li>}
                    {!s.prod.IsCAF && s.prod.CustomizationMethod === 2 && Portal?.PortalID === 0 && s.prod.ProductTypeID === 25 && (
                        <li>
                            Customize your Varsity Letter frame with <a href="/frame-designer/design-your-frame/4">Create-a-Frame</a>
                        </li>
                    )}

                    {s.prod.IsCAF && show_doc_size && <li>{`${s.prod.DocType} Size: ${s.ps.docWidth}"w x ${s.ps.docHeight}"h`}</li>}
                    {s.prod.IsCAF && show_frame_size && <li>{`Approx. Overall Frame Size: ${jf_width}"w x ${jf_height}"h`}</li>}

                    {s.prod.IsCAF && s.prod.LevelLock && !s.prod.HideLevelLock && Portal?.PortalID === 0 && (
                        <li>
                            Includes our easy-to-use <LevelLockModal />
                        </li>
                    )}

                    {s.prod.IsCAF && show_smart_box && Portal?.PortalID === 0 && (
                        <li>
                            This frame ships in a branded <SmartBoxModal />
                        </li>
                    )}

                    {s.prod.IsCAF && show_custom_frame && s.si.StoreTypeID === 5 && <li>This custom frame is handcrafted in the USA.</li>}
                    {s.prod.IsCAF && show_custom_frame && s.si.StoreTypeID !== 5 && <li>This custom frame is handcrafted in the USA and made-to-order.</li>}
                    {!s.ps.is_caf && !isNullOrWhiteSpace(s.ui.item_id) && <li>Item #: {s.ui.item_id}</li>}
                    {!s.ps.is_caf && !isNullOrWhiteSpace(s.ui.sku) && <li>SKU: {s.ui.sku}</li>}
                    {s.prod.IsCAF && s.prod.DYF_FrameStyleID === 10 && s.ps.docWidth <= 7 && <li>Includes easel back</li>}
                    {s.prod.IsCAF && <li>{s.prod.DYF_FrameStyleName}</li>}
                    {s.prod.IsCAF && <li>{s.ui.title_moulding} Moulding</li>}
                    {s.prod.IsCAF && <li>{s.ui.title_mat1} Top Mat</li>}
                    {s.prod.IsCAF && s.ps.mat2_id > 0 && <li>{s.ui.title_mat2} Inner Accent</li>}
                    {s.prod.IsCAF && s.ps.insignia_id > 0 && <li>{s.ui.title_insignia} Insignia</li>}
                    {s.prod.IsCAF && s.ps.lino_id > 0 && <li>{s.ps.lino_base_name} Embossed Text</li>}
                    {s.prod.IsCAF && s.ps.ep_id > 0 && <li>Engraved Plate</li>}
                    {s.prod.IsCAF && <li>{s.ps.is_horizontal ? 'Horizontal' : 'Vertical'} Orientation</li>}
                    {s.prod.IsCAF && s.ps.glass_id > 1 && (
                        <li>
                            {s.ui.name_glass} {s.prod.AllowGlassID1 === 2 || s.ps.use_plexi ? 'Plexiglass' : 'Glass'}
                        </li>
                    )}
                </ul>
            )}

            {(!hide_dynamic || Portal?.PortalID === 3) && s.si.has_bullet_override && s.si.default_prod_info_dynamic != null && s.si.default_prod_info_dynamic.length > 0 && (
                <ul>
                    {s.si.default_prod_info_dynamic.map((m, i) => (
                        <li key={i}>{m}</li>
                    ))}
                </ul>
            )}
        </>
    );
});

export default DescMainContent;
